import {CalendarProvider} from 'hubbl-shared';
import {firebaseFunctions} from './firebase';
import {httpsCallable} from 'firebase/functions';

interface InitiateOAuthResponse {
  authUrl: string;
  state: string;
}

interface ExchangeAuthCodeResponse {
  success: boolean;
  provider: CalendarProvider;
  connectedAt: string;
}

interface DisconnectCalendarResponse {
  success: boolean;
}

/**
 * Initiates calendar OAuth flow
 * @param provider - Calendar provider (e.g., 'google')
 * @param platform - Platform requesting OAuth ('web' or 'mobile')
 * @returns Authorization URL and state parameter
 */
const initiateCalendarOAuth = async (
  provider: CalendarProvider,
  platform: 'web' | 'mobile',
): Promise<InitiateOAuthResponse> => {
  const callable = httpsCallable<
    {provider: CalendarProvider; platform: 'web' | 'mobile'},
    InitiateOAuthResponse
  >(firebaseFunctions, 'onInitiateCalendarOAuth');

  const response = await callable({provider, platform});
  return response.data;
};

/**
 * Exchanges OAuth authorization code for tokens
 * @param code - Authorization code from OAuth callback
 * @param provider - Calendar provider
 * @param state - State parameter for CSRF protection
 * @param platform - Platform ('web' or 'mobile')
 * @returns Success status and connection info
 */
const exchangeAuthCode = async (
  code: string,
  provider: CalendarProvider,
  state: string,
  platform: 'web' | 'mobile',
): Promise<ExchangeAuthCodeResponse> => {
  const callable = httpsCallable<
    {
      code: string;
      provider: CalendarProvider;
      state: string;
      platform: 'web' | 'mobile';
    },
    ExchangeAuthCodeResponse
  >(firebaseFunctions, 'onExchangeCalendarAuthCode');

  const response = await callable({code, provider, state, platform});
  return response.data;
};

/**
 * Disconnects calendar integration
 * @param provider - Calendar provider to disconnect
 * @returns Success status
 */
const disconnectCalendar = async (
  provider: CalendarProvider,
): Promise<DisconnectCalendarResponse> => {
  const callable = httpsCallable<
    {provider: CalendarProvider},
    DisconnectCalendarResponse
  >(firebaseFunctions, 'onDisconnectCalendarIntegration');

  const response = await callable({provider});
  return response.data;
};

export const ApiCalendar = {
  initiateCalendarOAuth,
  exchangeAuthCode,
  disconnectCalendar,
};
